import { Location } from 'history';
import { useEffect, useRef, useState } from 'react';
import { agentioInit, agentioSetupCallbacks } from '../lib/analytics/agentio';
import { analyticsCoordinator } from '../lib/analytics/coordinator';
import { fbInit, fbSetupCallbacks } from '../lib/analytics/fb';
import hotjar from '../lib/analytics/HotJar';
import { northbeamInit } from '../lib/analytics/northbeam';
import { podscribeSetupCallbacks } from '../lib/analytics/podscribe';
import { rosterSetupCallbacks } from '../lib/analytics/roster';
import { vibeSetupCallbacks, vibeTrackPageView } from '../lib/analytics/vibe';

/**
 * - loading: Segment's analytics.ready() has not been called yet
 * - waiting: Waiting for Optimize to potentially do its magic in the background.
 *   At this stage, we show the loading indicator but also render the page in the
 *   background, behind the async-hide style.
 * - done: Done.
 */
type AnalyticsState = 'loading' | 'waiting' | 'done';

export default function useAnalytics(location: Location, sessionInitialized: boolean) {
  const [analyticsState, setAnalyticsState] = useState<AnalyticsState>('loading');

  // Kick off analytics on mount
  useEffect(() => {
    if (sessionInitialized && analyticsState === 'loading') {
      // Registered before start() because start() fires the first page() call, and the pixel's
      // PageView hook has to be listening by then to catch it. Safe to hoist: this only registers
      // listeners and fires nothing on its own — each handler re-reads targeting consent when an
      // event actually arrives.
      fbSetupCallbacks();

      analyticsCoordinator
        .on('ready', () => setAnalyticsState('waiting'))
        .on('finished', () => {
          setAnalyticsState('done');

          // This doesn't make any tracking calls unless the user has granted targeting consent.
          // It allows events to be queued up until consent is granted.
          agentioSetupCallbacks();
          vibeSetupCallbacks();
          podscribeSetupCallbacks();
          rosterSetupCallbacks();

          // A callback that will be called as soon as all of our enabled analytics.js integrations have loaded.
          window.analytics.ready(() => {
            if (window.analytics && typeof window.analytics.user === 'function') {
              fbInit();

              // Northbeam pixel integration
              northbeamInit();

              // Agentio pixel integration for CTC attribution
              agentioInit();

              const anonymousId = window.analytics.user().anonymousId();
              hotjar.identify(anonymousId);
            }
          });
        })
        .start();
    }
  }, [analyticsState, sessionInitialized]);

  // Navigation hooks (before page change, after page change)
  const [handledLocation, setHandledLocation] = useState(location);
  if (location !== handledLocation) {
    analyticsCoordinator.beforePageChange();
    setHandledLocation(location);
  }
  // Only fire pageChange() *after* component is mounted.
  // https://stackoverflow.com/a/53351556/1480571
  const mounted = useRef(false);
  useEffect(() => {
    if (mounted.current) {
      analyticsCoordinator.pageChange();
      // Vibe page_view on SPA navigation (initial view fires from the base loader in index.html)
      vibeTrackPageView();
    } else {
      mounted.current = true;
    }
  }, [location]);

  return { analyticsState };
}
